import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { authGetRequest } from "../../api/authClient";
import { resolveWebSocketGatewayOrigin } from "../../runtime/loadSharedDashboardConfig";

export interface CameraItem {
  id: string;
  name: string;
  status: "Online" | "Offline";
  /** Last status change timestamp, if provided by API */
  sinceStatus?: string | null;
  /** Latest heartbeat / last seen timestamp */
  timestamp: string;
}

/** Analytic camera/by-client API response entry */
interface AnalyticCameraEntry {
  cameraId: string;
  clientId: string;
  locationId: string;
  locationName: string;
  name: string;
  cameraStatus: number;
  timestamp: string;
  sinceStatus?: string | null;
  aliasName?: string;
}

/** Analytic camera/by-client API response */
interface AnalyticCameraResponse {
  response: AnalyticCameraEntry[];
  totalCount: number;
  page: number;
  limit: number;
}

/** Map analytic API entry to CameraItem */
function mapAnalyticEntryToCameraItem(entry: AnalyticCameraEntry): CameraItem {
  return {
    id: entry.cameraId,
    name: entry.aliasName ?? entry.locationName ?? entry.name,
    status: entry.cameraStatus === 1 ? "Online" : "Offline",
    sinceStatus: entry.sinceStatus ?? null,
    timestamp: entry.timestamp,
  };
}

/** Get clientId from localStorage (same priority as rest of app) */
function getClientId(): string {
  try {
    const direct = localStorage.getItem("auth_user_id");
    if (direct) return direct;
    const raw = localStorage.getItem("auth_user");
    if (raw) return JSON.parse(raw)?.id ?? "";
  } catch {
    /* ignore */
  }
  return "";
}

/** Fetch cameras from analytic API - uses cookies (no token in header) */
async function fetchAnalyticCameras(
  clientId: string,
  page: number,
  limit: number
): Promise<{ items: CameraItem[]; totalCount: number }> {
  const now = new Date();
  const to = now.toISOString();
  const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .replace(/\.\d{3}Z$/, ".000Z");

  const res = (await authGetRequest<AnalyticCameraResponse>(
    "/analytic/camera/by-client",
    {
      clientId,
      from,
      to,
      page,
      limit,
    }
  )) as AnalyticCameraResponse;

  const items = (res.response ?? []).map(mapAnalyticEntryToCameraItem);
  return { items, totalCount: res.totalCount ?? 0 };
}

/** Fetch cameras by location - uses cookies (no token in header) */
async function fetchAnalyticCamerasByLocation(
  locationName: string,
  page: number,
  limit: number
): Promise<{ items: CameraItem[]; totalCount: number }> {
  const res = (await authGetRequest<AnalyticCameraResponse>(
    "/analytic/camera/by-location",
    {
      locationName: locationName.trim(),
      page,
      limit,
    }
  )) as AnalyticCameraResponse;

  const items = (res.response ?? []).map(mapAnalyticEntryToCameraItem);
  return { items, totalCount: res.totalCount ?? 0 };
}

/** Camera status list API response entry (same shape as history). */
interface CameraStatusApiEntry {
  id: string;
  camera_id?: string;
  cameraId?: string;
  client_id?: string;
  clientId?: string;
  location_id?: string;
  locationId?: string;
  location_name?: string;
  locationName?: string;
  camera_status?: number;
  cameraStatus?: number;
  name: string;
  aliasName?: string;
  timestamp: string;
  since_status?: string | null;
  sinceStatus?: string | null;
}

/** WebSocket: connected message */
interface WsConnectedMessage {
  type: "connected";
  client_id: string;
  message: string;
}

/** WebSocket: camera_status message (data same shape as CameraStatusApiEntry[]) */
interface WsCameraStatusMessage {
  type: "camera_status";
  data: CameraStatusApiEntry[];
  timestamp?: string;
}

type WsMessage = WsConnectedMessage | WsCameraStatusMessage;

const CAMERA_LIST_PAGE_SIZE = 20;

function getStatusStyles(status: CameraItem["status"]): string {
  switch (status) {
    case "Online":
      return "border-emerald-500/25 bg-emerald-500/15 text-emerald-800 dark:border-emerald-500/20 dark:text-emerald-300";
    case "Offline":
      return "border-red-500/25 bg-red-500/15 text-red-800 dark:border-red-500/20 dark:text-red-300";
    default:
      return "border-slate-500/25 bg-slate-500/15 text-slate-700 dark:border-slate-500/20 dark:text-slate-300";
  }
}

/** Format ISO timestamp for display (e.g. "22 Feb 2026, 10:45 am") */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

/** Duration from given ISO timestamp to now (e.g. "5h", "2h 30m", "45m", "3d 2h") */
function getDurationSince(iso: string): string {
  try {
    const start = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = now - start;
    if (diffMs < 0) return "0m";
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "<1m";
    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days}d`;
    return `${days}d ${remainingHours}h`;
  } catch {
    return "";
  }
}

/** Camera history API response entry */
export interface CameraHistoryApiEntry {
  id: string;
  camera_id: string;
  client_id: string;
  location_id: string;
  location_name: string;
  camera_status: number;
  name: string;
  timestamp: string;
}

/** Analytic by-camera API response (same shape as by-client) */
interface AnalyticByCameraResponse {
  response: AnalyticCameraEntry[];
  totalCount: number;
  page: number;
  limit: number;
}

/** Map analytic by-camera entry to CameraHistoryApiEntry */
function mapByCameraEntryToHistory(entry: AnalyticCameraEntry): CameraHistoryApiEntry {
  return {
    id: `${entry.cameraId}-${entry.timestamp}`,
    camera_id: entry.cameraId,
    client_id: entry.clientId,
    location_id: entry.locationId,
    location_name: entry.locationName ?? "",
    camera_status: entry.cameraStatus,
    name: entry.aliasName ?? entry.name ?? "",
    timestamp: entry.timestamp,
  };
}

/** Map websocket camera status entry to CameraItem */
function mapWsCameraEntryToCameraItem(entry: CameraStatusApiEntry): CameraItem {
  const statusCode = entry.camera_status ?? entry.cameraStatus ?? 0;
  const cameraId = entry.camera_id ?? entry.cameraId ?? entry.id;
  const locationName = entry.location_name ?? entry.locationName ?? "";
  const sinceStatus = entry.since_status ?? entry.sinceStatus ?? null;

  return {
    id: cameraId,
    name: entry.aliasName ?? locationName ?? entry.name ?? cameraId,
    status: statusCode === 1 ? "Online" : "Offline",
    sinceStatus,
    timestamp: entry.timestamp,
  };
}

function disposeWebSocket(socket: WebSocket | null) {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;
  const { readyState } = socket;
  if (readyState === WebSocket.CONNECTING) {
    socket.addEventListener(
      "open",
      () => {
        try {
          socket.close();
        } catch {
          // ignore
        }
      },
      { once: true }
    );
  } else if (readyState === WebSocket.OPEN) {
    try {
      socket.close();
    } catch {
      // ignore
    }
  }
}

/** Fetch camera history from analytic by-camera API */
async function fetchCameraHistoryByCamera(
  cameraId: string,
  page: number,
  limit: number
): Promise<{ items: CameraHistoryApiEntry[]; totalCount: number }> {
  const now = new Date();
  const to = now.toISOString();
  const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .replace(/\.\d{3}Z$/, ".000Z");

  const res = (await authGetRequest<AnalyticByCameraResponse>(
    "/analytic/camera/by-camera",
    { cameraId, from, to, page, limit }
  )) as AnalyticByCameraResponse;

  const items = (res.response ?? []).map(mapByCameraEntryToHistory);
  return { items, totalCount: res.totalCount ?? 0 };
}

const HISTORY_PAGE_SIZE = 5;

/** Map API entry to display event and type. */
function getHistoryEventLabel(entry: CameraHistoryApiEntry): string {
  return entry.camera_status === 1 ? "Status: Online" : "Status: Offline";
}

export const CameraStatus: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [cameras, setCameras] = useState<CameraItem[]>([]);
  const [cameraPage, setCameraPage] = useState(1);
  const [cameraTotalCount, setCameraTotalCount] = useState(0);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraLoadingMore, setCameraLoadingMore] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);

  const [wsConnected, setWsConnected] = useState(false);
  const [wsCameras, setWsCameras] = useState<CameraItem[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  const [selectedCamera, setSelectedCamera] = useState<CameraItem | null>(null);
  const [historyEntries, setHistoryEntries] = useState<CameraHistoryApiEntry[]>(
    []
  );
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const historyScrollRef = useRef<HTMLDivElement>(null);

  const cameraTotalPages = Math.ceil(cameraTotalCount / CAMERA_LIST_PAGE_SIZE);

  const loadCameraPage = useCallback(
    async (page: number, append: boolean, locationName?: string) => {
      if (locationName?.trim()) {
        // Search mode: use by-location API
        if (append) {
          setCameraLoadingMore(true);
        } else {
          setCameraLoading(true);
        }
        setCameraError(null);
        try {
          const { items, totalCount } = await fetchAnalyticCamerasByLocation(
            locationName.trim(),
            page,
            CAMERA_LIST_PAGE_SIZE
          );
          setCameraTotalCount(totalCount);
          if (append) {
            setCameras((prev) => [...prev, ...items]);
          } else {
            setCameras(items);
          }
          setCameraPage(page);
        } catch (e) {
          setCameraError(
            e instanceof Error ? e.message : "Failed to load cameras"
          );
          if (!append) setCameras([]);
        } finally {
          setCameraLoading(false);
          setCameraLoadingMore(false);
        }
      } else {
        // No search: use by-client API
        const clientId = getClientId();
        if (!clientId) {
          setCameraError("Please sign in to view cameras");
          setCameras([]);
          setCameraTotalCount(0);
          return;
        }
        if (append) {
          setCameraLoadingMore(true);
        } else {
          setCameraLoading(true);
        }
        setCameraError(null);
        try {
          const { items, totalCount } = await fetchAnalyticCameras(
            clientId,
            page,
            CAMERA_LIST_PAGE_SIZE
          );
          setCameraTotalCount(totalCount);
          if (append) {
            setCameras((prev) => [...prev, ...items]);
          } else {
            setCameras(items);
          }
          setCameraPage(page);
        } catch (e) {
          setCameraError(
            e instanceof Error ? e.message : "Failed to load cameras"
          );
          if (!append) setCameras([]);
        } finally {
          setCameraLoading(false);
          setCameraLoadingMore(false);
        }
      }
    },
    []
  );

  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Load cameras once on mount and when search changes (defer to avoid StrictMode double-call)
  useEffect(() => {
    const t = setTimeout(() => {
      loadCameraPage(1, false, debouncedSearch || undefined);
    }, 0);
    return () => clearTimeout(t);
  }, [debouncedSearch, loadCameraPage]);

  // --- WebSocket live camera feed ---
  // Flow: show REST data first; once WS is connected and receives camera_status,
  // UI switches to live list.
  useEffect(() => {
    let cancelled = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let ws: WebSocket | null = null;

    const clientId = getClientId();
    if (!clientId) {
      setWsConnected(false);
      setWsCameras([]);
      return;
    }

    const connect = () => {
      if (cancelled) return;
      const wsBase = resolveWebSocketGatewayOrigin();

      const now = new Date();
      const to = now.toISOString().replace(/\.\d{3}Z$/, ".999Z");
      const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .replace(/\.\d{3}Z$/, ".000Z");

      const url = new URL(`${wsBase.replace(/\/$/, "")}/analytic/camera-live`);
      url.searchParams.set("clientId", clientId);
      url.searchParams.set("page", "1");
      url.searchParams.set("limit", "200");
      url.searchParams.set("from", from);
      url.searchParams.set("to", to);

      try {
        ws = new WebSocket(url.toString());
        wsRef.current = ws;
      } catch {
        return;
      }

      ws.onmessage = (event) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(event.data as string) as WsMessage;
          if (msg.type === "connected") {
            setWsConnected(true);
            return;
          }
          if (msg.type === "camera_status" && Array.isArray(msg.data)) {
            setWsCameras(msg.data.map(mapWsCameraEntryToCameraItem));
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        // keep REST data as fallback
      };

      ws.onclose = () => {
        if (cancelled) return;
        setWsConnected(false);
        retryTimeoutId = setTimeout(() => {
          if (!cancelled) connect();
        }, 3000);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimeoutId != null) clearTimeout(retryTimeoutId);
      disposeWebSocket(ws);
      ws = null;
      wsRef.current = null;
      setWsConnected(false);
    };
  }, []);

  const isSearchMode = debouncedSearch.length > 0;
  // Source priority:
  // 1) Search mode => show by-location API data only
  // 2) Non-search mode => prefer WebSocket live data, fallback to API data
  const useWsForDisplay = !isSearchMode && wsConnected && wsCameras.length > 0;
  const displayedCameras = useMemo(
    () => (useWsForDisplay ? wsCameras : cameras),
    [useWsForDisplay, wsCameras, cameras]
  );
  const hasMoreCameras = !useWsForDisplay && cameras.length < cameraTotalCount;

  const onListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el || cameraLoadingMore || cameraLoading) return;
    if (useWsForDisplay) return; // no pagination when showing WS data
    if (!hasMoreCameras || cameraPage >= cameraTotalPages) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      const locationName = debouncedSearch.trim() || undefined;
      loadCameraPage(cameraPage + 1, true, locationName);
    }
  }, [
    useWsForDisplay,
    cameraLoadingMore,
    cameraLoading,
    hasMoreCameras,
    cameraPage,
    cameraTotalPages,
    debouncedSearch,
    loadCameraPage,
  ]);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedCamera(null);
    };
    if (selectedCamera) {
      document.addEventListener("keydown", onEscape);
      return () => document.removeEventListener("keydown", onEscape);
    }
  }, [selectedCamera]);

  const hasMoreHistory = historyEntries.length < historyTotalCount;
  const totalPages = Math.ceil(historyTotalCount / HISTORY_PAGE_SIZE);

  const loadHistoryPage = useCallback(
    async (cameraId: string, page: number, append: boolean) => {
      if (append) {
        setHistoryLoadingMore(true);
      } else {
        setHistoryLoading(true);
      }
      setHistoryError(null);
      try {
        const { items, totalCount } = await fetchCameraHistoryByCamera(
          cameraId,
          page,
          HISTORY_PAGE_SIZE
        );
        setHistoryTotalCount(totalCount);
        if (append) {
          setHistoryEntries((prev) => [...prev, ...items]);
        } else {
          setHistoryEntries(items);
        }
        setHistoryPage(page);
      } catch (e) {
        setHistoryError(
          e instanceof Error ? e.message : "Failed to load history"
        );
        if (!append) setHistoryEntries([]);
      } finally {
        setHistoryLoading(false);
        setHistoryLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!selectedCamera) {
      setHistoryEntries([]);
      setHistoryPage(1);
      setHistoryTotalCount(0);
      setHistoryError(null);
      return;
    }
    loadHistoryPage(selectedCamera.id, 1, false);
  }, [selectedCamera, loadHistoryPage]);

  /** Check if we're at bottom (or content doesn't overflow) and load more */
  const checkHistoryLoadMore = useCallback(() => {
    const el = historyScrollRef.current;
    if (!el || !selectedCamera || historyLoadingMore || historyLoading) return;
    if (!hasMoreHistory || historyPage >= totalPages) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    // Load more when: scrolled near bottom, OR content doesn't fill viewport (no scroll possible)
    const atBottom = scrollTop + clientHeight >= scrollHeight - 80;
    const noOverflow = scrollHeight <= clientHeight;
    if (atBottom || noOverflow) {
      loadHistoryPage(selectedCamera.id, historyPage + 1, true);
    }
  }, [
    selectedCamera,
    historyLoadingMore,
    historyLoading,
    hasMoreHistory,
    historyPage,
    totalPages,
    loadHistoryPage,
  ]);

  const onHistoryScroll = useCallback(() => {
    checkHistoryLoadMore();
  }, [checkHistoryLoadMore]);

  // Auto-load more when content doesn't fill viewport (e.g. only 5 items, no scrollbar)
  useEffect(() => {
    if (!selectedCamera || !hasMoreHistory) return;
    const timer = requestAnimationFrame(() => {
      checkHistoryLoadMore();
    });
    return () => cancelAnimationFrame(timer);
  }, [
    selectedCamera,
    historyEntries.length,
    historyTotalCount,
    historyLoading,
    historyLoadingMore,
    hasMoreHistory,
    checkHistoryLoadMore,
  ]);

  return (
    <div className="flex h-full w-full flex-col p-5">
      {/* Fixed header: title + search — stays visible while scrolling */}
      <div className="sticky top-0 z-10 -mx-5 -mt-5 flex-shrink-0 border-b border-slate-200 bg-white px-5 pb-4 pt-5 dark:border-slate-700/50 dark:bg-[#0B0F19]">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Cameras</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            List of cameras with status
          </p>
        </div>
        {/* Search by location: GET /analytic/camera/by-location?locationName=... */}
        <div className="mt-4 flex items-center gap-3">
          <label htmlFor="camera-search" className="sr-only">
            Search by location name
          </label>
          <input
            id="camera-search"
            type="text"
            placeholder="Search by location name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-10 max-w-md flex-1 rounded-lg border border-slate-300 bg-white px-4 text-slate-900 placeholder:text-slate-500 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 dark:border-[#334155] dark:bg-[#1C202F] dark:text-slate-100 dark:placeholder:text-slate-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="h-10 rounded-lg px-3 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Scrollable card area */}
      <div
        ref={listScrollRef}
        onScroll={onListScroll}
        className="mt-4 max-h-[calc(100vh-14rem)] min-h-0 flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {cameraError && (
          <p className="mb-3 text-sm text-red-600 dark:text-red-400">{cameraError}</p>
        )}
        {!useWsForDisplay && cameraLoading && displayedCameras.length === 0 ? (
          <div className="grid grid-cols-1 gap-4 pb-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700/80 dark:bg-[#1C202F]/55"
                aria-hidden
              >
                <div className="absolute left-0 right-0 top-0 h-1 animate-pulse bg-slate-300 dark:bg-slate-600" />
                <div className="p-4 pt-5">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 flex-shrink-0 animate-pulse rounded-lg bg-slate-300 dark:bg-slate-600" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="h-4 w-3/4 animate-pulse rounded bg-slate-300 dark:bg-slate-600" />
                      <div className="h-4 w-1/2 animate-pulse rounded bg-slate-300 dark:bg-slate-600" />
                      <div className="mt-2 h-6 w-20 animate-pulse rounded-md bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : displayedCameras.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-700/80 dark:bg-[#1C202F]/45">
            <p className="text-slate-600 dark:text-slate-400">
              {isSearchMode
                ? `No cameras match "${debouncedSearch}"`
                : "No cameras found"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 pb-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {displayedCameras.map((camera, index) => (
              <div
                key={`${camera.id ?? "camera"}-${index}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedCamera(camera)}
                onKeyDown={(e) => e.key === "Enter" && setSelectedCamera(camera)}
                className={`
                  relative cursor-pointer overflow-hidden rounded-xl border
                  border-slate-200 bg-white
                  transition-all duration-200 hover:border-slate-300 hover:shadow-md
                  dark:border-slate-700/80 dark:bg-[#1C202F]/55 dark:hover:border-slate-600/90
                  ${camera.status === "Online" ? "ring-1 ring-emerald-500/20 dark:ring-emerald-500/10" : ""}
                `}
              >
                {/* Status indicator bar */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1 ${
                    camera.status === "Online" ? "bg-emerald-400" : "bg-red-400"
                  }`}
                />
                <div className="p-4 pt-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 flex items-start gap-3">
                      <div
                        className={`
                          flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg
                          ${camera.status === "Online" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-red-500/15 text-red-700 dark:text-red-400"}
                        `}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-semibold text-slate-900 dark:text-white">
                          {camera.name}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-lg text-xs font-semibold border ${getStatusStyles(
                            camera.status
                          )}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              camera.status === "Online"
                                ? "bg-emerald-500"
                                : "bg-red-500"
                            }`}
                          />
                          {camera.status}
                          {(camera.sinceStatus ?? camera.timestamp) && (
                            <>
                              <span className="opacity-70">·</span>
                              <span>
                                {getDurationSince(
                                  camera.sinceStatus ?? camera.timestamp
                                )}
                              </span>
                            </>
                          )}
                        </span>
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                          {camera.sinceStatus
                            ? `${camera.status} since ${formatTimestamp(camera.sinceStatus)}`
                            : `${camera.status} since ${formatTimestamp(camera.timestamp)}`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="pb-2 pt-2 text-xs text-slate-600 dark:text-slate-500">
          {useWsForDisplay
            ? `Showing ${displayedCameras.length} cameras (live)${isSearchMode ? ` · search "${debouncedSearch}"` : ""}`
            : `Showing ${displayedCameras.length} of ${cameraTotalCount} cameras${isSearchMode ? ` · search "${debouncedSearch}"` : ""}`}
        </p>
        {cameraLoadingMore && (
          <p className="pb-2 text-xs text-slate-600 dark:text-slate-400">Loading more…</p>
        )}
      </div>

      {/* Camera history popup */}
      {selectedCamera && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedCamera(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="camera-history-title"
        >
          <div
            className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700/80 dark:bg-[#1C202F]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 p-4 dark:border-slate-700/70">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
                    selectedCamera.status === "Online"
                      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                      : "bg-red-500/15 text-red-700 dark:text-red-400"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2
                    id="camera-history-title"
                    className="truncate text-lg font-semibold text-slate-900 dark:text-white"
                  >
                    {selectedCamera.name}
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
                    Last seen: {formatTimestamp(selectedCamera.timestamp)} ·{" "}
                    {selectedCamera.status}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCamera(null)}
                className="flex-shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-white"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div
              ref={historyScrollRef}
              onScroll={onHistoryScroll}
              className="min-h-0 flex-1 overflow-y-auto p-4"
            >
              <h3 className="mb-3 text-sm font-semibold tracking-tight text-slate-800 dark:text-slate-300">
                Camera history
                {historyTotalCount > 0 && (
                  <span className="ml-2 font-normal text-slate-500">
                    ({historyEntries.length} of {historyTotalCount})
                  </span>
                )}
              </h3>
              {historyError && (
                <p className="mb-3 text-sm text-red-600 dark:text-red-400">{historyError}</p>
              )}
              {historyLoading && historyEntries.length === 0 ? (
                <p className="text-sm text-slate-600 dark:text-slate-400">Loading history…</p>
              ) : (
                <ul className="space-y-2">
                  {historyEntries.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-600/70 dark:bg-[#1C202F]"
                    >
                      <span className="min-w-[8.5rem] flex-shrink-0 whitespace-nowrap text-xs text-slate-500">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                      <span className="text-sm text-slate-900 dark:text-white">
                        {getHistoryEventLabel(entry)}
                      </span>
                      <span
                        className={[
                          "ml-auto flex-shrink-0 rounded border px-2 py-0.5 text-xs",
                          entry.camera_status === 1
                            ? "border-emerald-500/25 bg-emerald-500/15 text-emerald-800 dark:border-emerald-500/20 dark:text-emerald-400"
                            : "border-red-500/25 bg-red-500/15 text-red-800 dark:border-red-500/20 dark:text-red-400",
                        ].join(" ")}
                      >
                        {entry.camera_status === 1 ? "Online" : "Offline"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {historyLoadingMore && (
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">Loading more…</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraStatus;
